import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Search, Sparkles, Package, Lock, X, Check, ChevronLeft, ChevronRight, Images as ImagesIcon } from "lucide-react";

/**
 * Pagina PUBBLICA "share outlet" — accessibile solo via token nell'URL,
 * SENZA registrazione, SENZA navbar/footer del sito, SENZA accesso ad altre rotte.
 * Mostra l'outlet (flash + stock) con Listino + Prezzo Studio per il cliente finale.
 * Il visitatore può inoltre PRENOTARE un prodotto per 48h inserendo email/telefono.
 *
 * Path: /share/:token
 */
const fmtEUR = (n) => `€ ${Math.round(Number(n) || 0).toLocaleString("it-IT")}`;

function ProductCard({ p, isFlash = false, flashDiscount = 0, onReserve, onOpen }) {
  const token = useParams().token;
  const firstImage = p?.images?.[0];
  const imgUrl = firstImage
    ? `${process.env.REACT_APP_BACKEND_URL}/api/share/${token}/products/images/${firstImage.id}?v=${firstImage.size || 0}`
    : null;
  const imgCount = (p?.images?.length || 0);
  const listino = Number(p.price_retail || 0);
  const studio = p.price_offer != null ? Number(p.price_offer) : null;
  const discount = (listino > 0 && studio != null && studio < listino)
    ? Math.round((1 - studio / listino) * 100)
    : null;
  const reservedUntil = p.reservation_expires_at;
  const isReserved = !!reservedUntil;

  return (
    <div
      className={`bg-white border rounded-sm overflow-hidden transition-all duration-300 group ${
        isReserved ? "border-rose-200 opacity-75" : "border-ink/10 hover:border-ink/30"
      }`}
      data-testid={`share-product-${p.id}`}
    >
      <button
        type="button"
        onClick={() => onOpen?.(p)}
        className="aspect-[4/3] w-full bg-cream2 overflow-hidden relative block cursor-zoom-in"
        aria-label={`Apri galleria ${p.name}`}
        data-testid={`share-product-open-${p.id}`}
      >
        {imgUrl ? (
          <img
            src={imgUrl}
            alt={p.name}
            loading="lazy"
            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate1">
            <Package size={32} />
          </div>
        )}
        {isFlash && (
          <div className="absolute top-3 left-3 bg-rose-700 text-cream text-[10px] tracking-[0.2em] uppercase px-2 py-1 rounded-sm inline-flex items-center gap-1">
            <Sparkles size={10} /> Flash {flashDiscount > 0 ? `−${flashDiscount}%` : ""}
          </div>
        )}
        {!isFlash && discount && discount >= 5 && !isReserved && (
          <div className="absolute top-3 left-3 bg-gold text-ink text-[10px] tracking-[0.2em] uppercase px-2 py-1 rounded-sm">
            −{discount}%
          </div>
        )}
        {isReserved && (
          <div className="absolute top-3 left-3 bg-rose-700 text-cream text-[10px] tracking-[0.2em] uppercase px-2 py-1 rounded-sm inline-flex items-center gap-1">
            <Lock size={10} /> Riservato
          </div>
        )}
        {imgCount > 1 && (
          <div
            className="absolute bottom-3 right-3 bg-ink/80 text-cream text-[10px] tracking-[0.2em] uppercase px-2 py-1 rounded-sm inline-flex items-center gap-1 backdrop-blur-sm"
            data-testid={`share-product-imgcount-${p.id}`}
          >
            <ImagesIcon size={10} /> {imgCount} foto
          </div>
        )}
      </button>
      <div className="p-4 md:p-5 space-y-2">
        {p.brand && (
          <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">{p.brand}</div>
        )}
        <div className="font-display text-lg text-ink leading-tight">{p.name}</div>
        {p.category && (
          <div className="text-xs text-slate1 capitalize">{p.category}</div>
        )}
        <div className="pt-2 border-t border-ink/10 space-y-1">
          <div className="flex items-baseline justify-between">
            <span className="text-[10px] tracking-[0.15em] uppercase text-slate1">Listino</span>
            <span className={`tabular-nums ${studio != null ? "text-slate1 line-through text-sm" : "text-ink font-medium"}`}>
              {fmtEUR(listino)}
            </span>
          </div>
          {studio != null && (
            <div className="flex items-baseline justify-between">
              <span className="text-[10px] tracking-[0.15em] uppercase text-gold">Offerta</span>
              <span className="font-display text-2xl text-gold tabular-nums">{fmtEUR(studio)}</span>
            </div>
          )}
          <div className="text-[10px] text-slate1 italic pt-1">IVA inclusa · Trasporto e montaggio incluso entro 50 km</div>
        </div>

        {/* Action: Reserve 48h or "Riservato fino a..." */}
        <div className="pt-3">
          {isReserved ? (
            <div className="w-full py-2.5 bg-rose-50 border border-rose-200 text-rose-700 text-center text-[11px] tracking-[0.15em] uppercase rounded-sm" data-testid={`reserved-until-${p.id}`}>
              Riservato fino al {new Date(reservedUntil).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => onReserve(p)}
              className="w-full py-2.5 bg-ink text-cream text-[11px] tracking-[0.2em] uppercase rounded-sm hover:bg-gold hover:text-ink transition-colors"
              data-testid={`reserve-btn-${p.id}`}
            >
              Prenota per 48 ore
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ReserveModal({ product, token, onClose, onSuccess }) {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    if (!email && !phone) {
      setError("Inserisci almeno una email o un numero di telefono.");
      return;
    }
    if (email && !email.includes("@")) {
      setError("Email non valida.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/share/${token}/reserve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product_id: product.id,
          customer_email: email.trim(),
          customer_phone: phone.trim(),
          customer_name: name.trim(),
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        if (res.status === 409) {
          setError("Questo prodotto è appena stato prenotato da un altro utente.");
        } else if (res.status === 429) {
          setError("Troppe prenotazioni dalla stessa email. Riprova domani.");
        } else {
          setError(j.detail || "Errore durante la prenotazione.");
        }
      } else {
        const j = await res.json();
        setSuccess(j);
        onSuccess?.(j);
      }
    } catch {
      setError("Errore di connessione.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="reserve-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-cream w-full max-w-md rounded-sm overflow-hidden">
        <div className="p-5 border-b border-ink/10 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-1">Prenotazione 48 ore</div>
            <div className="font-display text-xl text-ink leading-tight">
              {product.brand ? `${product.brand} · ` : ""}{product.name}
            </div>
          </div>
          <button onClick={onClose} className="text-slate1 hover:text-ink" aria-label="Chiudi" data-testid="reserve-close">
            <X size={18} />
          </button>
        </div>

        {success ? (
          <div className="p-6 text-center" data-testid="reserve-success">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-emerald-100 flex items-center justify-center">
              <Check size={24} className="text-emerald-600" />
            </div>
            <div className="font-display text-xl text-ink mb-2">Prenotazione confermata</div>
            <div className="text-sm text-slate1 leading-relaxed mb-4">
              Hai riservato <strong>{success.product_brand} · {success.product_name}</strong> per 48 ore.
              <br />
              Lo studio ti contatterà al più presto per confermare i dettagli.
            </div>
            <div className="text-[11px] tracking-[0.15em] uppercase text-slate1">
              Scadenza: {new Date(success.expires_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}
            </div>
            <button
              onClick={onClose}
              className="mt-5 w-full py-2.5 bg-ink text-cream text-[11px] tracking-[0.2em] uppercase rounded-sm hover:bg-ink/90"
              data-testid="reserve-success-close"
            >
              Chiudi
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-3">
            <p className="text-sm text-slate1 leading-relaxed">
              Lasciaci un recapito: lo studio ti contatterà entro 48 ore per confermare il prodotto.
            </p>
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">Nome (opzionale)</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-white text-sm focus:outline-none focus:border-ink"
                data-testid="reserve-name"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@esempio.it"
                className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-white text-sm focus:outline-none focus:border-ink"
                data-testid="reserve-email"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">Telefono</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="333 123 4567"
                className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-white text-sm focus:outline-none focus:border-ink"
                data-testid="reserve-phone"
              />
            </div>
            <div className="text-[11px] text-slate1 italic">
              Almeno uno tra email o telefono è obbligatorio.
            </div>
            {error && (
              <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-sm px-3 py-2" data-testid="reserve-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 bg-ink text-cream text-[11px] tracking-[0.2em] uppercase rounded-sm hover:bg-gold hover:text-ink transition-colors disabled:opacity-50"
              data-testid="reserve-submit"
            >
              {submitting ? "Invio..." : "Prenota per 48 ore"}
            </button>
            <div className="text-[10px] text-slate1 leading-relaxed pt-1">
              Inviando questi dati acconsenti ad essere contattato dallo studio per la conferma della prenotazione.
              I dati non saranno usati per altre finalità.
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/**
 * Lightbox/galleria: click sull'immagine card → modal con TUTTE le foto del prodotto,
 * navigazione prev/next + thumbnails + tastiera (←/→/Esc). Su mobile swipe-friendly.
 */
function GalleryModal({ product, token, onClose, onReserve }) {
  const images = product?.images || [];
  const [idx, setIdx] = useState(0);

  const urls = useMemo(
    () => images.map((i) => `${process.env.REACT_APP_BACKEND_URL}/api/share/${token}/products/images/${i.id}?v=${i.size || 0}`),
    [images, token],
  );

  const next = useCallback(() => {
    if (urls.length > 1) setIdx((i) => (i + 1) % urls.length);
  }, [urls.length]);

  const prev = useCallback(() => {
    if (urls.length > 1) setIdx((i) => (i - 1 + urls.length) % urls.length);
  }, [urls.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [next, prev, onClose]);

  const listino = Number(product.price_retail || 0);
  const studio = product.price_offer != null ? Number(product.price_offer) : null;
  const reservedUntil = product.reservation_expires_at;
  const isReserved = !!reservedUntil;

  return (
    <div
      className="fixed inset-0 z-50 bg-ink/90 backdrop-blur-sm flex flex-col"
      onClick={onClose}
      data-testid="share-gallery-modal"
    >
      {/* Header */}
      <div className="px-5 py-4 flex items-start justify-between gap-4 text-cream" onClick={(e) => e.stopPropagation()}>
        <div className="min-w-0">
          {product.brand && <div className="text-[10px] tracking-[0.3em] uppercase text-gold">{product.brand}</div>}
          <div className="font-display text-xl md:text-2xl leading-tight truncate">{product.name}</div>
        </div>
        <button
          onClick={onClose}
          className="shrink-0 w-10 h-10 flex items-center justify-center text-cream/70 hover:text-cream hover:bg-cream/10 rounded-sm"
          aria-label="Chiudi"
          data-testid="share-gallery-close"
        >
          <X size={20} />
        </button>
      </div>

      {/* Main image area */}
      <div
        className="flex-1 relative flex items-center justify-center min-h-0 px-4 md:px-12"
        onClick={(e) => e.stopPropagation()}
      >
        {urls.length === 0 ? (
          <div className="text-cream/60 inline-flex items-center gap-2">
            <Package size={24} /> Nessuna immagine
          </div>
        ) : (
          <img
            src={urls[idx]}
            alt={`${product.name} (${idx + 1}/${urls.length})`}
            className="max-w-full max-h-full object-contain"
            data-testid="share-gallery-image"
          />
        )}

        {urls.length > 1 && (
          <>
            <button
              onClick={prev}
              className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 bg-cream/10 hover:bg-cream/20 text-cream rounded-full flex items-center justify-center backdrop-blur-sm"
              aria-label="Precedente"
              data-testid="share-gallery-prev"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              onClick={next}
              className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 bg-cream/10 hover:bg-cream/20 text-cream rounded-full flex items-center justify-center backdrop-blur-sm"
              aria-label="Successiva"
              data-testid="share-gallery-next"
            >
              <ChevronRight size={22} />
            </button>
            <div
              className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-ink/70 text-cream text-[11px] tracking-[0.2em] px-3 py-1 rounded-sm tabular-nums"
              data-testid="share-gallery-counter"
            >
              {idx + 1} / {urls.length}
            </div>
          </>
        )}
      </div>

      {/* Thumbnails strip */}
      {urls.length > 1 && (
        <div
          className="px-4 md:px-12 pt-2 pb-3 overflow-x-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex gap-2 justify-center">
            {urls.map((u, i) => (
              <button
                key={u}
                onClick={() => setIdx(i)}
                className={`flex-shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-sm overflow-hidden border-2 transition-all ${
                  i === idx ? "border-gold opacity-100" : "border-cream/20 opacity-50 hover:opacity-80"
                }`}
                aria-label={`Foto ${i + 1}`}
                data-testid={`share-gallery-thumb-${i}`}
              >
                <img src={u} alt="" loading="lazy" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Footer with price + reserve CTA */}
      <div className="bg-cream px-5 py-4 flex items-center justify-between gap-4 flex-wrap" onClick={(e) => e.stopPropagation()}>
        <div className="min-w-0">
          {studio != null ? (
            <>
              <div className="text-[10px] tracking-[0.2em] uppercase text-slate1 line-through">Listino {fmtEUR(listino)}</div>
              <div className="font-display text-2xl md:text-3xl text-gold tabular-nums">{fmtEUR(studio)}</div>
            </>
          ) : (
            <>
              <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">Listino</div>
              <div className="font-display text-2xl md:text-3xl text-ink tabular-nums">{fmtEUR(listino)}</div>
            </>
          )}
          <div className="text-[10px] text-slate1 italic">IVA inclusa · Trasporto e montaggio incluso entro 50 km</div>
        </div>
        {isReserved ? (
          <div className="px-4 py-2.5 bg-rose-50 border border-rose-200 text-rose-700 text-center text-[11px] tracking-[0.15em] uppercase rounded-sm">
            Riservato fino al {new Date(reservedUntil).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => { onClose(); onReserve(product); }}
            className="px-6 py-3 bg-ink text-cream text-[11px] tracking-[0.2em] uppercase rounded-sm hover:bg-gold hover:text-ink transition-colors"
            data-testid="share-gallery-reserve"
          >
            Prenota per 48 ore
          </button>
        )}
      </div>
    </div>
  );
}

export default function SharedOutlet() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [reserveTarget, setReserveTarget] = useState(null);
  const [galleryTarget, setGalleryTarget] = useState(null);

  const reload = useCallback(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/share/${token}/outlet`)
      .then(async (r) => {
        if (r.status === 410) {
          setError("Questo link è stato disattivato.");
          return;
        }
        if (!r.ok) {
          setError("Link non valido o scaduto.");
          return;
        }
        const j = await r.json();
        setData(j);
      })
      .catch(() => setError("Errore di connessione."))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { reload(); }, [reload]);

  const filtered = useMemo(() => {
    if (!data) return { flash: [], products: [] };
    const k = query.trim().toLowerCase();
    const m = (p) => {
      if (!k) return true;
      return (
        (p.name || "").toLowerCase().includes(k) ||
        (p.brand || "").toLowerCase().includes(k) ||
        (p.category || "").toLowerCase().includes(k)
      );
    };
    return {
      flash: (data.flash_sales || []).filter((fs) => fs.product && m(fs.product)),
      // Prodotti con foto prima, quelli senza foto sempre in fondo (ordine stabile)
      products: (data.products || []).filter(m).slice().sort((a, b) => {
        const ha = (a.images?.length || 0) > 0 ? 0 : 1;
        const hb = (b.images?.length || 0) > 0 ? 0 : 1;
        return ha - hb;
      }),
    };
  }, [data, query]);

  if (loading) {
    return (
      <div className="min-h-[100svh] bg-cream flex items-center justify-center text-slate1 text-sm tracking-[0.15em] uppercase">
        Caricamento offerte…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[100svh] bg-cream flex items-center justify-center p-8">
        <div className="text-center max-w-md" data-testid="share-error">
          <div className="font-display text-2xl text-ink mb-3">Offerta non disponibile</div>
          <div className="text-sm text-slate1 leading-relaxed">{error}</div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-slate1 mt-8">ARCHI-HUB</div>
        </div>
      </div>
    );
  }

  const totalCount = (filtered.flash?.length || 0) + (filtered.products?.length || 0);

  return (
    <div className="min-h-[100svh] bg-cream" data-testid="share-outlet">
      <header className="bg-ink text-cream py-8 md:py-10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-[10px] tracking-[0.4em] uppercase text-gold mb-2">ARCHI-HUB</div>
          <h1 className="font-display text-3xl md:text-4xl lg:text-5xl leading-tight">
            Outlet riservato
          </h1>
          {data?.name && (
            <div className="mt-2 text-sm text-cream/70 italic">{data.name}</div>
          )}
          <div className="mt-4 text-xs text-cream/60 max-w-xl leading-relaxed">
            Selezione esclusiva ARCHI-HUB · prezzi <span className="text-cream">IVA inclusa</span> ·
            trasporto e montaggio inclusi entro 50&nbsp;km. Puoi <span className="text-gold">prenotare un prodotto per 48 ore</span> lasciando un recapito.
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 md:py-14">
        <div className="mb-8 md:mb-10 max-w-xl">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate1" size={16} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca per nome, brand, categoria..."
              className="w-full pl-10 pr-4 py-3 border border-ink/20 bg-white rounded-sm text-sm focus:outline-none focus:border-ink"
              data-testid="share-search"
            />
          </div>
        </div>

        {totalCount === 0 ? (
          <div className="text-center py-20 text-slate1 italic">
            Nessun prodotto disponibile al momento.
          </div>
        ) : (
          <>
            {filtered.flash.length > 0 && (
              <section className="mb-12 md:mb-16" data-testid="share-flash-section">
                <div className="flex items-center gap-2 mb-5">
                  <Sparkles size={14} className="text-rose-700" />
                  <span className="text-[11px] tracking-[0.3em] uppercase text-rose-700 font-medium">
                    Flash · disponibilità limitata
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
                  {filtered.flash.map((fs) => (
                    <ProductCard
                      key={fs.id}
                      p={fs.product}
                      isFlash
                      flashDiscount={fs.discount_pct || 0}
                      onReserve={setReserveTarget}
                      onOpen={setGalleryTarget}
                    />
                  ))}
                </div>
              </section>
            )}

            {filtered.products.length > 0 && (
              <section data-testid="share-stock-section">
                <div className="flex items-center gap-2 mb-5">
                  <Package size={14} className="text-ink" />
                  <span className="text-[11px] tracking-[0.3em] uppercase text-ink font-medium">
                    Disponibili in stock
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
                  {filtered.products.map((p) => (
                    <ProductCard key={p.id} p={p} onReserve={setReserveTarget} onOpen={setGalleryTarget} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>

      <footer className="bg-cream border-t border-ink/10 mt-12 py-8">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <div className="text-[10px] tracking-[0.4em] uppercase text-slate1">
            ARCHI-HUB · Cataloghi e listini riservati per architetti
          </div>
        </div>
      </footer>

      {reserveTarget && (
        <ReserveModal
          product={reserveTarget}
          token={token}
          onClose={() => setReserveTarget(null)}
          onSuccess={() => { reload(); }}
        />
      )}

      {galleryTarget && (
        <GalleryModal
          product={galleryTarget}
          token={token}
          onClose={() => setGalleryTarget(null)}
          onReserve={setReserveTarget}
        />
      )}
    </div>
  );
}
